import { useEffect, useState } from 'react'

import {
  type Pim3qsdnVideoV1,
  type PimProductImageV11,
} from '@redteclab/api/clients/bully'

interface CarouselGalleryControlsProps {
  images: PimProductImageV11[]
  initialImageIndex: number
  onIndexChanged: ((index: number) => void) | undefined
  onVideoBadgeClicked?: (scrollToVideo?: boolean) => void
  videos: Pim3qsdnVideoV1[]
}

interface CarouselGalleryControls {
  currentImageIndex: number
  handleGallerySideButtonClick: (nextIndex: number) => void
  handleImageScrolledIntoView: (index: number) => void
  handleRequestJumpToIndex: (imageIndex: number) => void
  handleVideoBadgeClick: (scrollToVideo?: boolean) => void
}

export const useCarouselGalleryControls = ({
  images,
  initialImageIndex,
  onIndexChanged,
  onVideoBadgeClicked,
  videos,
}: CarouselGalleryControlsProps): CarouselGalleryControls => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex)
  const [externalIndexChange, setExternalIndexChange] = useState(false)

  const notifyIndexChanged = (index: number): void => {
    if (onIndexChanged) {
      onIndexChanged(index)
    }
  }

  const handleRequestJumpToIndex = (imageIndex: number): void => {
    setExternalIndexChange(true)
    setCurrentImageIndex(imageIndex)
    notifyIndexChanged(imageIndex)
  }

  const handleImageScrolledIntoView = (index: number): void => {
    if (externalIndexChange) {
      if (index === currentImageIndex) {
        setExternalIndexChange(false)
      }
    } else {
      setCurrentImageIndex(index)
      notifyIndexChanged(index)
    }
  }

  const handleGallerySideButtonClick = (nextIndex: number): void => {
    if (nextIndex >= 0 && nextIndex < images.length + videos.length) {
      handleRequestJumpToIndex(nextIndex)
    }
  }

  const handleVideoBadgeClick = (scrollToVideo?: boolean): void => {
    if (scrollToVideo) {
      handleRequestJumpToIndex(images.length)
    }
    if (onVideoBadgeClicked) {
      onVideoBadgeClicked(scrollToVideo)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
    setExternalIndexChange(true)
  }, [initialImageIndex])

  return {
    currentImageIndex,
    handleGallerySideButtonClick,
    handleImageScrolledIntoView,
    handleRequestJumpToIndex,
    handleVideoBadgeClick,
  }
}
